<template>
  <b-row style="margin-top: 1em">
    <b-col cols="12" md="6">
      <b-card bg-variant="light" style="min-height: 140px" class="m-1">
        <b-row>
          <b-col>
            <h6 style="margin-top: 4px" class="text-info">
              {{ $t("globals.product") }}:
            </h6>
          </b-col>
          <b-col>
            <b-form-input
              :placeholder="device.product"
              size="sm"
              disabled
            />
          </b-col>
        </b-row>
        <b-row>
          <b-col>
            <h6 style="margin-top: 4px" class="text-info">
              {{ $t("globals.status") }}:
            </h6>
          </b-col>
          <b-col>
            <b-form-input :placeholder="device.status" size="sm" disabled />
          </b-col>
        </b-row>
        <b-row>
          <b-col>
            <h6 style="margin-top: 4px" class="text-info">
              {{ $t("globals.batch") }}:
            </h6>
          </b-col>
          <b-col>
            <b-form-input
              :placeholder="device.batch"
              size="sm"
              disabled
            />
          </b-col>
        </b-row>
      </b-card>
    </b-col>
    <b-col cols="12" md="6">
      <b-card bg-variant="light" style="min-height: 140px" class="m-1">
        <b-row>
          <b-col>
            <h6 style="margin-top: 4px" class="text-info">
              {{ $t("globals.current") + " " + $t("globals.customer") }}:
            </h6>
          </b-col>
          <b-col v-if="device.customer">
            <router-link
              style="color:#17a2b8"
              :to="{name: 'customer', params:{ id: device.customer.id }}"
            >
              <i><u>{{ device.customer.company_name }}</u></i>
            </router-link>
          </b-col>
        </b-row>
        <b-row>
          <b-col>
            <h6 style="margin-top: 4px" class="text-info">
              {{ $t("globals.current") + " " + $t("globals.order") }}:
            </h6>
          </b-col>
          <b-col v-if="device.order">
            <router-link
              style="color:#17a2b8"
              :to="{name: 'order', params:{ id: device.order.id }}"
            >
              <i><u>{{ $t("globals.order_no") + ": " + device.order.order_no }}</u></i>
            </router-link>
          </b-col>
        </b-row>
        <b-row>
          <b-col>
            <h6 style="margin-top: 4px" class="text-info">
              {{ $t("globals.current") + " " + $t("globals.brand") }}:
            </h6>
          </b-col>
          <b-col v-if="device.brand">
            <ulu-logo :brand="device.brand" scale="1.5"/>
          </b-col>
        </b-row>
      </b-card>
    </b-col>
    <b-col cols="12" v-if="device.contract && device.contract.contract_type">
      <b-card bg-variant="light" class="m-1">
        <h5 class="text-info">{{ $t("globals.contract_details") }}</h5>
        <div class="d-flex flex-wrap align-items-center" >
          <span class="text-info pr-2">{{ $t("globals.contract_type") }}:</span>
          <span class="mr-4">{{ device.contract.contract_type }}</span>

          <span class="text-info pr-2">{{ $t("globals.start_date") }}:</span>
          <span class="mr-4">{{ device.contract.start_date }}</span>

          <span class="text-info pr-2">{{ $t("globals.contract_duration") }}:</span>
          <span class="mr-4">{{ device.contract.duration }}</span>

          <span class="text-info pr-2">{{ $t("globals.contract_termination_policy") }}:</span>
          <span class="mr-4">{{ device.contract.termination_policy }}</span>

          <template v-if="device.subscription_price">
            <span class="text-info pr-2">{{ $t("globals.monthly_price") }}:</span>
            <span
              v-if="device.subscription_price.status === 'ok'"
              id="subscription-price-value"
              class="mr-4"
              style="cursor: pointer; text-decoration: underline dotted;"
              tabindex="0"
            >
              {{ currencyFormat(device.subscription_price.price_per_month_cents) }}
            </span>
            <span
              v-else
              class="mr-4"
              v-b-tooltip.hover
              :title="subscriptionPriceReasonText"
            >
              <v-icon name="exclamation-triangle" color="orange"></v-icon>
            </span>
            <b-popover
              v-if="device.subscription_price.status === 'ok'"
              target="subscription-price-value"
              triggers="click blur"
              placement="top"
            >
              <div v-if="device.subscription_price.order">
                <span class="text-info pr-2">{{ $t("globals.order") }}:</span>
                <router-link
                  :to="{ name: 'order', params: { id: device.subscription_price.order.id } }"
                >
                  {{ device.subscription_price.order.id }}
                </router-link>
              </div>
              <div v-if="device.subscription_price.order_item">
                <span class="text-info pr-2">{{ $t("globals.item_name") }}:</span>
                {{ device.subscription_price.order_item.item_name }}
              </div>
              <div v-if="device.subscription_price.order_item">
                <span class="text-info pr-2">{{ $t("globals.kpi_type") }}:</span>
                {{ device.subscription_price.order_item.kpi_type }}
              </div>
            </b-popover>
          </template>
        </div>
      </b-card>
    </b-col>
  </b-row>
</template>

<script>
import { mapGetters, mapMutations } from "vuex";
import { currencyFormat } from "@/helpers/price";

export default {
  components: {
    "ulu-logo": () => import("@/components/common/Logo.vue")
  },
  methods: {
    currencyFormat
  },
  computed: {
    subscriptionPriceReasonText() {
      const reason = this.device.subscription_price && this.device.subscription_price.reason;
      const key = "globals.price_reason_" + reason;
      return this.$te(key) ? this.$t(key) : this.$t("globals.price_reason_unknown");
    }
  },
  props: {
    refresh: Function,
    loading: Boolean,
    device: {
      type: Object,
      default: () => ({
        status: "",
        created_at: "",
        product: "",
        batch: "",
        brand: null,
        order: {
          id: null,
          order_no: 0
        },
        customer: {
          id: null,
          company_name: ""
        },
        contract: {
          contract_type: "",
          start_date: "",
          duration: "",
          termination_policy: ""
        },
        subscription_price:{
          status: "ok",
          reason: null,
          price_per_month_cents: 0,
          order: {
            id: null
          },
          order_item: {
            id: null,
            item_name: "",
            kpi_type: ""
          }
        }

        
      })
    }
  }
};
</script>
